/**
 * iter1238 P1c · useStreamingParsedPreview
 * =========================================
 * Frontend hook that polls the /upload-jobs/{jobId}/parsed-preview
 * endpoint while a streaming-pipeline upload job is in flight and
 * appends new rows to a local buffer.
 *
 * Adaptive backoff: 2s → 5s → 10s → 30s → 60s, mirroring the polling
 * shape from BackgroundJobsContext.
 *
 * Stops polling automatically when:
 *   - the job reaches a terminal status (completed, failed, cancelled)
 *   - the endpoint reports streaming_supported=false (legacy job)
 *   - the parent component unmounts
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const nextDelay = (elapsedMs) => {
  const hidden = typeof document !== 'undefined' && document.hidden;
  let base;
  if (elapsedMs < 30_000) base = 2_000;
  else if (elapsedMs < 120_000) base = 5_000;
  else if (elapsedMs < 300_000) base = 10_000;
  else if (elapsedMs < 900_000) base = 30_000;
  else base = 60_000;
  return hidden ? Math.max(base, 60_000) : base;
};

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export const useStreamingParsedPreview = (jobId, options = {}) => {
  const { enabled = true, onComplete } = options;
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    pending: 0, processing: 0, committed: 0, failed: 0,
  });
  const [jobStatus, setJobStatus] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [streamingSupported, setStreamingSupported] = useState(true);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const startMsRef = useRef(Date.now());
  const sinceRef = useRef(-1);
  const mountedRef = useRef(true);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    startMsRef.current = Date.now();
    sinceRef.current = -1;

    if (!jobId || !enabled) {
      stop();
      return () => { mountedRef.current = false; stop(); };
    }

    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const response = await axios.get(
          `${API}/api/bank-feeds/upload-jobs/${jobId}/parsed-preview`,
          { params: { since_row_index: sinceRef.current, limit: 500 } }
        );
        if (!mountedRef.current) return;
        const data = response.data || {};

        setStreamingSupported(!!data.streaming_supported);
        setJobStatus(data.job_status || null);
        setProgressPercent(data.progress_percent || 0);
        setStatusMessage(data.status_message || '');
        if (data.counts) setCounts(data.counts);

        const newRows = Array.isArray(data.rows) ? data.rows : [];
        if (newRows.length > 0) {
          setRows((prev) => [...prev, ...newRows]);
          sinceRef.current = data.next_since_row_index;
        }

        // Stop polling if done or not-supported.
        if (!data.streaming_supported) { stop(); return; }
        if (data.job_status && TERMINAL_STATUSES.has(data.job_status)) {
          stop();
          if (onCompleteRef.current) onCompleteRef.current(data);
          return;
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err?.response?.data?.detail || err?.message || 'poll failed');
        // Transient errors: keep polling with a slightly longer delay.
      }
      if (!mountedRef.current) return;
      const delay = nextDelay(Date.now() - startMsRef.current);
      timerRef.current = setTimeout(poll, delay);
    };

    // Kick off immediately.
    timerRef.current = setTimeout(poll, 0);

    return () => { mountedRef.current = false; stop(); };
  }, [jobId, enabled, stop]);

  // Re-poll immediately when tab becomes visible.
  useEffect(() => {
    const onVis = () => {
      if (typeof document === 'undefined' || document.hidden) return;
      if (!jobId || !enabled) return;
      stop();
      const delay = 0;
      timerRef.current = setTimeout(() => {
        startMsRef.current = Date.now();
      }, delay);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
      return () => document.removeEventListener('visibilitychange', onVis);
    }
    return undefined;
  }, [jobId, enabled, stop]);

  return {
    rows,
    counts,
    jobStatus,
    progressPercent,
    statusMessage,
    streamingSupported,
    error,
    isPolling: !!timerRef.current,
  };
};

export default useStreamingParsedPreview;
